/**
 * Process-lifetime store for minted simulation asset credentials (OMG-2277).
 *
 * Split out of `components/Simulation/simAssetAuth` for dependency direction:
 * sign-out has to drop these credentials, and having `lib/authentication` import
 * from `components/` pointed the layering backwards. Minting, the GraphQL query
 * and the React hook stay in the component module; only the state lives here.
 *
 * Module scope rather than React state on purpose: a technique page renders one
 * poster per simulation, and all of them plus the engine must share a single
 * mint — six posters must not fire six mints, and the engine then asks ~4,000
 * times per package load.
 */

export interface SimAssetAuthorization {
  baseUrl: string;
  version: string;
  policy: string;
  signature: string;
  keyPairId: string;
  expiresInMs: number;
}

interface CacheEntry {
  authorization: SimAssetAuthorization;
  /** Local-clock deadline, derived from a duration so device skew can't skew it. */
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<SimAssetAuthorization>>();

// Page-wide dedupe for reporting a failed mint. It belongs with the cache because
// the mint is the shared cause: a technique page with six simulations makes one
// request, so all six posters fail together and one cause should be one report.
let mintFailureReported = false;

export const getCachedAuthorization = (version: string) => cache.get(version);

export const setCachedAuthorization = (
  version: string,
  authorization: SimAssetAuthorization,
) => {
  cache.set(version, {
    authorization,
    expiresAt: Date.now() + authorization.expiresInMs,
  });
  // A success re-arms failure reporting, so a recovery followed by a fresh
  // outage still reports instead of being swallowed as a duplicate.
  mintFailureReported = false;
};

export const getInFlightMint = (version: string) => inFlight.get(version);

export const setInFlightMint = (
  version: string,
  mint: Promise<SimAssetAuthorization>,
) => {
  inFlight.set(version, mint);
};

export const clearInFlightMint = (version: string) => {
  inFlight.delete(version);
};

/**
 * Drops cached credentials so the next call re-mints.
 *
 * Called on sign-out, not only from tests: the cache is keyed by asset version
 * alone, so on a shared device the next member would otherwise ride the previous
 * member's policy for up to 12 h. That was harmless while the API gated on
 * authentication alone, and stopped being harmless on 2026-07-28 when
 * omgyes-backend#62 grew an entitlement check — a signed-out member's policy
 * would carry their entitlement to whoever signed in next.
 */
export const clearSimAssetAuthorizations = () => {
  cache.clear();
  inFlight.clear();
  mintFailureReported = false;
};

/** Returns true for the first caller after a success, false for the rest. */
export const claimMintFailureReport = (): boolean => {
  if (mintFailureReported) return false;
  mintFailureReported = true;
  return true;
};
